import { useEffect, useState } from "react";
import { User } from "@/types/user";
import { HealthcareDomain } from "@/types/core";
import { UpdateUserHealthcareDomainData } from "../mutations/updateUserDomain";

export const useUpdateHealthcareDomainManager = (user: User | null) => {
  const [userHealthcareDomains, setUserHealthcareDomains] = useState<
    User["healthcare_domains"]
  >([]);
  const [selectedHealthcareDomains, setSelectedHealthcareDomains] = useState<
    HealthcareDomain[]
  >([]);
  const [healthcareDomainDataObject, setHealthcareDomainDataObject] = useState<
    UpdateUserHealthcareDomainData[]
  >([]);

  useEffect(() => {
    if (user) {
      const domains = user.healthcare_domains || [];
      setUserHealthcareDomains(domains);
      setSelectedHealthcareDomains(domains.map(uhd => uhd.healthcare_domain));
    }
  }, [user]);

  useEffect(() => {
    if (!user) return;

    const selectedIds = selectedHealthcareDomains.map(d => d.id);
    const originalIds = user.healthcare_domains.map(
      uhd => uhd.healthcare_domain.id,
    );

    const additions: UpdateUserHealthcareDomainData[] = selectedIds
      .filter(id => !originalIds.includes(id))
      .map(id => ({
        healthcare_domain_id: id,
        years_of_experience: null,
        remove_healthcare_domain: false,
      }));

    const removals: UpdateUserHealthcareDomainData[] = originalIds
      .filter(id => !selectedIds.includes(id))
      .map(id => ({
        healthcare_domain_id: id,
        remove_healthcare_domain: true,
      }));

    const retained: UpdateUserHealthcareDomainData[] = user.healthcare_domains
      .filter(uhd => selectedIds.includes(uhd.healthcare_domain.id))
      .map(uhd => ({
        healthcare_domain_id: uhd.healthcare_domain.id,
        years_of_experience: uhd.years_of_experience,
        remove_healthcare_domain: false,
      }));

    setHealthcareDomainDataObject([...retained, ...additions, ...removals]);
  }, [selectedHealthcareDomains, user]);

  const healthcareDomainsHaveChanged = () => {
    const original = userHealthcareDomains
      .map(uhd => uhd.healthcare_domain.id)
      .sort();
    const current = selectedHealthcareDomains.map(d => d.id).sort();
    return (
      original.length !== current.length ||
      original.some((id, i) => id !== current[i])
    );
  };

  const resetHealthcareDomains = () => {
    if (!user) return;
    const domains = user.healthcare_domains || [];
    setUserHealthcareDomains(domains);
    setSelectedHealthcareDomains(domains.map(uhd => uhd.healthcare_domain));
    setHealthcareDomainDataObject([]);
  };

  return {
    userHealthcareDomains,
    selectedHealthcareDomains,
    setSelectedHealthcareDomains,
    healthcareDomainDataObject,
    healthcareDomainsHaveChanged,
    resetHealthcareDomains,
  };
};
